import React from 'react';
import {
  Text,
  DefaultButton,
  PrimaryButton,
  Toggle,
  Dialog,
  DialogFooter,
  DialogType,
  Icon,
} from '@fluentui/react';

import SystemClient from '../../system/SystemClient';
import MessageCourier from '../../lib/MessageCourier';
import useAsyncDataCourier from '../../common/custom-hooks/useAsyncDataCourier';
import { ContextualMenu } from '@fluentui/react/lib/ContextualMenu';

import { useCustomInput } from '../../common/custom-hooks/useCustomInput';
import CustomInputIfc from '../../lib/FormValidator/Form/CustomInputIfc';
import PlanillaDigitalForm, {
  ROL_JUGADOR,
  DURACION_SANCION_EN_SEGUNDOS,
  ROL_OFICIAL,
  ROL_OFICIAL_AUXILIAR,
} from './PlanillaDigitalForm';
import {
  GOL,
  PENAL,
  TARJETA_AMARILLA,
  DOS_MINUTOS,
  TARJETA_ROJA,
  TARJETA_AZUL,
} from '../../system/TipoEventoPlanilla/TipoEventoPlanilla';
import { EventoXPlanilla } from '../../system/EventoXPlanilla/EventoXPlanilla';
import { PlanillaPartido } from '../../system/PlanillaPartido/PlanillaPartido';
import PlanillaDigitalTimer from './PlanillaDigitalTimer';
import PlanillaDigitalUndo from './PlanillaDigitalUndo';
import PlanillaDigitalJugadoresDetailList from './PlanillaDigitalJugadoresDetailList';
import PlanillaDigitalOficialesDetailList from './PlanillaDigitalOficialesDetailList';
import PlanillaDigitalExclusionesDetailList from './PlanillaDigitalExclusionesDetailList';
import PlanillaDigitalTeamCard from './PlanillaDigitalTeamCard';
import PlanillaDigitalEventButtons from './PlanillaDigitalEventButtons';
import PlanillaDigitalJugadoresGrid from './PlanillaDigitalJugadoresGrid';
import { TimeoutXPlanilla } from '../../system/TimeoutXPlanilla/TimeoutXPlanilla';
import PlanillaDigitalTimeoutDetailList from './PlanillaDigitalTimeoutDetailList';
import PlanillaDigitalGoalsDetailList from './PlanillaDigitalGoalsDetailList';
import { PlanillaPartidoOficial } from '../../system/PlanillaPartidoOficial/PlanillaPartidoOficial';
import { PlanillaPartidoJugador } from '../../system/PlanillaPartidoJugador/PlanillaPartidoJugador';
import { PlanillaPartidoOficialAuxiliar } from '../../system/PlanillaPartidoOficialAuxiliar/PlanillaPartidoOficialAuxiliar';
import PlanillaDigitalEventList from './PlanillaDigitalEventList';
import { useTimer } from 'react-timer-hook';
import { FINALIZADO, SUSPENDIDO } from './PlanillaDigitalViewController';
import { useContainerStyle } from '../../common/custom-hooks/useStyles';

interface IPlanillaDigitalView {
  system: SystemClient;
  courier: MessageCourier;
  changeBlockUI: (state: boolean) => void;
  form: PlanillaDigitalForm;
  onBlurCustomInput: (i: CustomInputIfc) => void;
  viewMode: string;
  localColor: string;
  visitanteColor: string;
  isPartidoOnline: boolean;
  countdownMode: boolean;
  invertedTeams: boolean;
  matchIsOver: boolean;
  setMatchIsOver: (v: boolean) => void;
  onRefereeReview: boolean;
  archivoSyncDescargado: boolean;
  setOnRefereeReview: (v: boolean) => void;
  iniciarPartido: () => Promise<void>;
  descargarArchivoJSON: (suspendido: boolean) => void;
  setShowConfirmarPlanillaDialog: (v: boolean, tipo: string, minuto: string) => void;
  startInExtra: boolean;
  tiempoPendienteJuegoEnSegundos: number;
  useIndicaDuracionPeriodos: boolean;
  shouldRetrySaveEvents: boolean;
  shouldShowDescargarArchivoJSON: boolean;
  shouldShowPDF: boolean;
}

export interface IPeriodos {
  isPlaying: boolean;
  isFinished: boolean;
  required: boolean;
  durationInMinutes: number;
  desc: string;
  longDesc: string;
  isExtra: boolean;
  tiempo: number;
}

const PlanillaDigitalView = ({
  system,
  courier,
  changeBlockUI,
  form,
  onBlurCustomInput,
  viewMode,
  localColor,
  visitanteColor,
  isPartidoOnline,
  invertedTeams,
  countdownMode,
  matchIsOver,
  setMatchIsOver,
  onRefereeReview,
  setOnRefereeReview: setOnRevereeReview,
  iniciarPartido,
  setShowConfirmarPlanillaDialog: setShowFinalizarPartidoDialog,
  descargarArchivoJSON,
  setShowConfirmarPlanillaDialog,
  startInExtra,
  tiempoPendienteJuegoEnSegundos,
  useIndicaDuracionPeriodos,
  shouldRetrySaveEvents,
  shouldShowDescargarArchivoJSON,
  shouldShowPDF,
  ...props
}: IPlanillaDigitalView) => {
  const [tipoEventoSelected, setTipoEventoSelected] = React.useState<string>('');

  const planillaEventosInput = useCustomInput(() => form.getPlanillaEventoInput(), [form]);
  const planillaPartidoInput = useCustomInput(() => form.getPlanillaPartidoInput(), [form]);
  const timeoutPartidoInput = useCustomInput(() => form.getTimeoutXPlanillaInput(), [form]);

  const esDiferido = React.useMemo(() => {
    return !!planillaPartidoInput.getValue().getDiferido();
  }, [planillaPartidoInput]);

  const [periodos, setPeriodos] = React.useState<IPeriodos[]>([]);
  const [hasStarted, setHasStarted] = React.useState<boolean>(false);
  const [viewEvents, setViewEvents] = React.useState<boolean>(false);
  const [viewDts, setViewDts] = React.useState<boolean>(false);
  const [viewTimeouts, setViewTimeouts] = React.useState<boolean>(false);
  const [viewGoals, setViewGoals] = React.useState<boolean>(false);
  const [showFinalizarPeriodo, setShowFinalizarPeriodo] = React.useState<boolean>(false);
  const [periodoStarted, setPeriodoStarted] = React.useState<boolean>(false);

  const [showEndDialog, setShowEndDialog] = React.useState<boolean>(false);
  const [alertMsg, setAlertMsg] = React.useState<string>('');
  const toggleShowEndDialog = () => setShowEndDialog(!showEndDialog);

  const [showAlertDialog, setShowAlertDialog] = React.useState<boolean>(false);
  const [errorMsg, setErrorMsg] = React.useState<string>('');
  const toggleShowDialog = () => setShowAlertDialog(!showAlertDialog);

  const [showPenales, setShowPenales] = React.useState<boolean>(false);
  const [enablePenales, setEnablePenales] = React.useState<boolean>(false);

  const { totalSeconds, seconds, minutes, isRunning, start, pause, resume, restart } = useTimer({
    expiryTimestamp: new Date(),
    onExpire: () => setShowFinalizarPeriodo(true),
    autoStart: false,
  });

  const [eventCooldownActive, setEventCooldownActive] = React.useState<boolean>(false);
  const [lastEventTarget, setLastEventTarget] = React.useState<number | null>();

  React.useEffect(() => {
    saveEvents();
  }, [isRunning, shouldRetrySaveEvents]);

  React.useEffect(() => {
    const planillaPartido = planillaPartidoInput.getValue() as PlanillaPartido;
    const categoria = planillaPartido.getPartido().getTorneo()?.getCategoria();
    let periodos: IPeriodos[] = [];
    if (categoria) {
      for (let i = 0; i < categoria?.getCantPeriodos(); i++) {
        periodos.push({
          isPlaying: i === 0 ? true : false,
          isFinished: false,
          required: true,
          durationInMinutes: +categoria.getDuracionPeriodo(),
          desc: form.getPeriodDescription(i + 1, false),
          longDesc: form.getPeriodLongDescription(i + 1, false),
          isExtra: false,
          tiempo: periodos.length + 1,
        });
      }

      if (!planillaPartido.getPuedeEmpatar()) {
        for (let i = 0; i < categoria?.getCantPeriodosExtra(); i++) {
          periodos.push({
            isPlaying: false,
            isFinished: false,
            required: false,
            durationInMinutes: +categoria.getDuracionPeriodoExtra(),
            desc: form.getPeriodDescription(i + 1, true),
            longDesc: form.getPeriodLongDescription(i + 1, true),
            isExtra: true,
            tiempo: periodos.length + 1,
          });
        }
      }
    }
    const newTimer = new Date();
    newTimer.setSeconds(newTimer.getSeconds() + periodos[0].durationInMinutes * 60); // 10 minutes timer
    // newTimer.setSeconds(newTimer.getSeconds() + 5); // 10 minutes timer

    restart(newTimer, false);
    setPeriodos(periodos);
  }, []);

  // Este useEffect es para cuando indica el tiempo que falta jugar cuando viene de un partido suspendido.
  React.useEffect(() => {
    if (useIndicaDuracionPeriodos) {
      const planillaPartido = planillaPartidoInput.getValue() as PlanillaPartido;
      const categoria = planillaPartido.getPartido().getTorneo()?.getCategoria();
      let periodos: IPeriodos[] = [];
      let initialPeriod = 0;
      console.log('startInExtra > ', startInExtra);
      if (!startInExtra) {
        periodos.push(
          {
            isPlaying: true,
            isFinished: false,
            required: true,
            durationInMinutes: tiempoPendienteJuegoEnSegundos / 2 / 60,
            desc: form.getPeriodDescription(1, false),
            longDesc: form.getPeriodLongDescription(1, false),
            isExtra: false,
            tiempo: 1,
          },
          {
            isPlaying: false,
            isFinished: false,
            required: true,
            durationInMinutes: tiempoPendienteJuegoEnSegundos / 2 / 60,
            desc: form.getPeriodDescription(2, false),
            longDesc: form.getPeriodLongDescription(2, false),
            isExtra: false,
            tiempo: 2,
          }
        );

        if (!planillaPartido.getPuedeEmpatar() && categoria) {
          for (let i = 0; i < categoria?.getCantPeriodosExtra(); i++) {
            periodos.push({
              isPlaying: false,
              isFinished: false,
              required: false,
              durationInMinutes: +categoria.getDuracionPeriodoExtra(),
              desc: form.getPeriodDescription(i + 1, true),
              longDesc: form.getPeriodLongDescription(i + 1, true),
              isExtra: true,
              tiempo: periodos.length + 1,
            });
          }
        }
      } else {
        if (categoria) {
          for (let i = 0; i < categoria?.getCantPeriodos(); i++) {
            periodos.push({
              isPlaying: false,
              isFinished: true,
              required: true,
              durationInMinutes: +categoria.getDuracionPeriodo(),
              desc: form.getPeriodDescription(i + 1, false),
              longDesc: form.getPeriodLongDescription(i + 1, false),
              isExtra: false,
              tiempo: periodos.length + 1,
            });
          }
          initialPeriod = periodos.length + 1;

          periodos.push(
            {
              isPlaying: true,
              isFinished: false,
              required: false,
              durationInMinutes: tiempoPendienteJuegoEnSegundos / 2 / 60,
              desc: form.getPeriodDescription(3, true),
              longDesc: form.getPeriodLongDescription(3, true),
              isExtra: true,
              tiempo: 3,
            },
            {
              isPlaying: false,
              isFinished: false,
              required: false,
              durationInMinutes: tiempoPendienteJuegoEnSegundos / 2 / 60,
              desc: form.getPeriodDescription(4, true),
              longDesc: form.getPeriodLongDescription(4, true),
              isExtra: true,
              tiempo: 4,
            }
          );
        }
      }
      const newTimer = new Date();
      newTimer.setSeconds(newTimer.getSeconds() + periodos[initialPeriod].durationInMinutes * 60); // 10 minutes timer

      restart(newTimer, false);
      setPeriodos(periodos);
    }
  }, [tiempoPendienteJuegoEnSegundos]);

  const finalizarPeriodo = () => {
    if (esDiferido) {
      return;
    }
    pause(); // esto hace el trigger de saveEvents()
    const _periodos = [...periodos];
    const i = _periodos.findIndex((p) => p.isPlaying);
    _periodos[i].isFinished = true;
    _periodos[i].isPlaying = false;

    const planillaPartido = planillaPartidoInput.getValue() as PlanillaPartido;
    const debeDesempatar =
      // planillaPartido.getPartido().getTorneo()?.getTorneoTipo()?.isGrilla() ||
      !planillaPartido.getPuedeEmpatar();
    const periodosReglamentariosJugados = _periodos.filter((p) => p.required).every((p) => p.isFinished);
    const golesLocalTotal = form.getCantidadGolesLocal() + +planillaPartido.getPartido()!.getGolesLocal()!;
    const golesVisitanteTotal =
      form.getCantidadGolesVisitante() + +planillaPartido.getPartido()!.getGolesVisitante()!;
    const partidoEmpatado = !!(golesLocalTotal === golesVisitanteTotal);
    const newTimer = new Date();
    if (periodosReglamentariosJugados && debeDesempatar && partidoEmpatado) {
      if (_periodos[i + 1]) {
        // ##################### Si queda un tiempo extra ###########################
        _periodos[i + 1].isPlaying = true;
        newTimer.setSeconds(newTimer.getSeconds() + _periodos[i + 1].durationInMinutes * 60); // 10 minutes timer
        // newTimer.setSeconds(newTimer.getSeconds() + 5); // 10 minutes timer
        restart(newTimer, false);
        setAlertMsg('Fin del ' + _periodos[i].longDesc);
      } else {
        // ##################### Si el partido sigue empatado despues de los tiempos extras ###########################
        setShowPenales(true);
        // setMatchIsOver(true);
        setOnRevereeReview(true);
        setAlertMsg('Termino el partido');
      }
    } else {
      if (debeDesempatar) {
        if (_periodos[i].required && !_periodos[i + 1].required) {
          // Si esta en el ultimo tiempo reglamentario
          if (!partidoEmpatado) {
            // Si no esta empatado, termino el partido
            setMatchIsOver(true);
            setOnRevereeReview(true);
            setAlertMsg('Termino el partido');
          }
        } else if (_periodos[i + 1]) {
          _periodos[i + 1].isPlaying = true;
          newTimer.setSeconds(newTimer.getSeconds() + _periodos[i + 1].durationInMinutes * 60); // 10 minutes timer
          // newTimer.setSeconds(newTimer.getSeconds() + 5); // 10 minutes timer

          restart(newTimer, false);
          setAlertMsg('Fin del ' + _periodos[i].longDesc);
        } else {
          setMatchIsOver(true);
          setOnRevereeReview(true);
          setAlertMsg('Termino el partido');
        }
      } else {
        // ##################### Si el partido todavia tiene tiempos reglamentarios ###########################
        if (_periodos[i + 1] && _periodos[i + 1].required) {
          _periodos[i + 1].isPlaying = true;
          newTimer.setSeconds(newTimer.getSeconds() + _periodos[i + 1].durationInMinutes * 60); // 10 minutes timer
          // newTimer.setSeconds(newTimer.getSeconds() + 5); // 10 minutes timer
          restart(newTimer, false);
          setAlertMsg('Fin del ' + _periodos[i].longDesc);
        } else {
          // ##################### El partido terminó ###########################
          setMatchIsOver(true);
          setOnRevereeReview(true);
          setAlertMsg('Termino el partido');
        }
      }
    }
    setShowFinalizarPeriodo(false);
    setShowEndDialog(true);
    setPeriodos(_periodos);
    setPeriodoStarted(false);
  };

  const tipoEventoList = useAsyncDataCourier(
    () => system.getTipoEventoPlanillaSystem().getList(),
    [system],
    courier
  );

  const esSancionDeTiempo = () => tipoEventoSelected === DOS_MINUTOS || tipoEventoSelected === TARJETA_ROJA;

  const getMinuto = () => {
    const planillaPartido = planillaPartidoInput.getValue() as PlanillaPartido;
    const categoria = planillaPartido.getPartido().getTorneo()?.getCategoria();
    const periodosRequiredJugados = periodos.filter((p) => p.required && p.isFinished).length;
    const periodosExtrasJugados = periodos.filter((p) => !p.required && p.isFinished).length;
    if (!esDiferido) {
      if (enablePenales) {
        const SEGUNDO_EXTRA_PARA_INDICAR_PENALES = 1;
        const segTotalesTranscurridos =
          periodosRequiredJugados * +categoria?.getDuracionPeriodo()! * 60 +
          periodosExtrasJugados * +categoria?.getDuracionPeriodoExtra()! * 60 +
          SEGUNDO_EXTRA_PARA_INDICAR_PENALES;
        const _minutes = Math.floor(segTotalesTranscurridos / 60);
        const segs = segTotalesTranscurridos % 60;
        const minutoEvento = `${String(_minutes).padStart(2, '0')}:${String(segs).padStart(2, '0')}`;

        return minutoEvento;
      } else {
        const duracionPeriodo = periodos.find((p) => p.isPlaying)
          ? +periodos.find((p) => p.isPlaying)!.durationInMinutes
          : 0;
        const duracionEnSegundosPeriodo = duracionPeriodo * 60;
        const segundosTranscurridosDelPeriodo = duracionEnSegundosPeriodo - totalSeconds;
        const segTotalesTranscurridos =
          segundosTranscurridosDelPeriodo +
          periodosRequiredJugados * +categoria?.getDuracionPeriodo()! * 60 +
          periodosExtrasJugados * +categoria?.getDuracionPeriodoExtra()! * 60;

        const _minutes = Math.floor(segTotalesTranscurridos / 60);
        const segs = segTotalesTranscurridos % 60;

        const minutoEvento = `${String(_minutes).padStart(2, '0')}:${String(segs).padStart(2, '0')}`;

        return minutoEvento;
      }
    } else {
      const segs = 1;
      const _minutes =
        periodosRequiredJugados * +categoria?.getDuracionPeriodo()! +
        periodosExtrasJugados * +categoria?.getDuracionPeriodoExtra()!;
      const minutoEvento = `${String(_minutes).padStart(2, '0')}:${String(segs).padStart(2, '0')}`;

      return minutoEvento;
    }
    return '00:00';
  };

  const procesarSancionTiempo = (
    _planillaEventos: EventoXPlanilla[],
    id: number,
    rol: string,
    minutoActual: string
  ) => {
    let tiempoEnSegundos = esSancionDeTiempo() ? DURACION_SANCION_EN_SEGUNDOS : 0;

    if (esSancionDeTiempo()) {
      const _sancionVigente = _planillaEventos.find(
        (_e) =>
          ((rol === ROL_JUGADOR && _e.getPpjid() === id) ||
            (rol === ROL_OFICIAL && _e.getPpoid() === id) ||
            (rol === ROL_OFICIAL_AUXILIAR && _e.getPpoaid() === id)) &&
          _e.getTipoEventoPlanilla()?.getNombre() === DOS_MINUTOS &&
          _e.getTiempoRestante(minutoActual) > 0
      );

      if (_sancionVigente) {
        tiempoEnSegundos = tiempoEnSegundos + _sancionVigente.getTiempoRestante(minutoActual);
        _sancionVigente.setDuracionEnSegundos(
          _sancionVigente.getDuracionEnSegundos()! - _sancionVigente.getTiempoRestante(minutoActual)
        );
      }
    }
    return tiempoEnSegundos;
  };

  const [pendingCallbacksSaveEvents, setPendingCallbacksSaveEvents] = React.useState<number>(0);
  const [isUploading, setIsUploading] = React.useState<boolean>(false);

  const saveChangesInPlayers = async () => {
    const planillaPartido = form.getPlanillaPartidoInput().getValue() as PlanillaPartido;
    const pending = planillaPartido.getPlanillaPartidoJugadorList().filter((ppj) => ppj.hasPendingChanges());
    const pendingSavePromises = pending.map((ppj) => system.getPlanillaPartidoJugadorSystem().save(ppj));
    try {
      await Promise.all(pendingSavePromises);
    } catch (error) {
      console.error('error al guardar cambios en jugador :>> ', error);
    }
  };

  const containerStyle = useContainerStyle();

  const saveEventsRecursive = async () => {
    if (props.archivoSyncDescargado) return;
    setIsUploading(true);

    saveChangesInPlayers().finally(() => {});

    const listEvents = [...planillaEventosInput.getValue()] as EventoXPlanilla[];
    const listEventsPendings = listEvents.filter((e) => !e.isSaved());
    const listEventsPendingsIdxs = listEventsPendings.map((e) =>
      listEvents.findIndex((_e) => _e.getIdArtifical() === e.getIdArtifical())
    );

    const listTimeouts = [...timeoutPartidoInput.getValue()] as TimeoutXPlanilla[];
    const listTimeoutsPendings = listTimeouts.filter((t) => !t.isSaved());
    const listTimeoutsPendingsIdxs = listTimeoutsPendings.map((t) =>
      listTimeouts.findIndex((_t) => _t.getIdArtifical() === t.getIdArtifical())
    );

    const minutoActual = getMinuto();
    const planillaEnVivo = form.createScreenshot(minutoActual, !isRunning);

    try {
      const ret = await system.getEventoXPlanillaSystem().updatesForPlanilla({
        events: listEventsPendings,
        timeouts: listTimeoutsPendings,
        planillaEnVivo: planillaEnVivo,
      });

      ret.events.forEach((eventoGuardado, i) => {
        listEvents[listEventsPendingsIdxs[i]].saved(eventoGuardado);
      });

      ret.timeouts.forEach((e, i) => {
        listTimeouts[listTimeoutsPendingsIdxs[i]].saved(e);
      });
    } catch (error) {
      console.log(error);
    } finally {
      let count = 0;
      setPendingCallbacksSaveEvents((prevState) => {
        count = prevState - 1;
        return count;
      });

      if (count > 0) {
        await saveEventsRecursive();
      } else {
        planillaEventosInput.setValue([...planillaEventosInput.getValue()]);
        timeoutPartidoInput.setValue([...timeoutPartidoInput.getValue()]);
        setIsUploading(false);
      }
    }
  };

  const saveEvents = async () => {
    if (isPartidoOnline) {
      setPendingCallbacksSaveEvents((prevState) => prevState + 1);
      if (!isUploading) {
        return saveEventsRecursive();
      }
    }
  };

  React.useEffect(() => {
    if (eventCooldownActive) {
      setTimeout(() => {
        setEventCooldownActive(false);
      }, COOLDOWN_EVENTO);
    }
  }, [eventCooldownActive]);

  const showError = (msg: string) => {
    setErrorMsg(msg);
    setShowAlertDialog(true);
  };

  const addEventForPersona = async (
    rol: string,
    id: number,
    entity: PlanillaPartidoJugador | PlanillaPartidoOficial | PlanillaPartidoOficialAuxiliar
  ) => {
    if (props.archivoSyncDescargado) {
      return showError('No puede asignar eventos luego de la descarga del archivo de sincronización');
    }
    const planillaPartido = planillaPartidoInput.getValue() as PlanillaPartido;
    const ppId = planillaPartido.getId();
    const tipoEvento = tipoEventoList?.find((te) =>
      enablePenales && tipoEventoSelected === GOL
        ? te.getNombre() === PENAL
        : te.getNombre() === tipoEventoSelected
    );

    const minutoActual = getMinuto();

    setEventCooldownActive(true);
    console.log('periodoStarted > ', periodoStarted);

    if (onRefereeReview || (!matchIsOver && periodoStarted) || esDiferido || (matchIsOver && enablePenales)) {
      if (!eventCooldownActive || lastEventTarget !== id) {
        if (tipoEvento) {
          try {
            form.validateEvent(tipoEvento, id, rol, minutoActual, onRefereeReview);

            setLastEventTarget(id);

            const list = [...planillaEventosInput.getValue()] as EventoXPlanilla[];
            const minuto = procesarSancionTiempo(list, id, rol, minutoActual);

            form.isJugadorExcluido(id, minutoActual);

            if (rol === ROL_JUGADOR && entity instanceof PlanillaPartidoJugador) {
              let e = EventoXPlanilla.createForJugador(+ppId!, tipoEvento, minutoActual, id, minuto, entity);
              list.push(e);
              planillaEventosInput.setValue(list);
            }

            if (rol === ROL_OFICIAL && entity instanceof PlanillaPartidoOficial) {
              let e = EventoXPlanilla.createForOficial(+ppId!, tipoEvento, minutoActual, id, minuto, entity);
              list.push(e);
              planillaEventosInput.setValue(list);
            }

            if (rol === ROL_OFICIAL_AUXILIAR && entity instanceof PlanillaPartidoOficialAuxiliar) {
              let e = EventoXPlanilla.createForOficialAuxiliar(
                +ppId!,
                tipoEvento,
                minutoActual,
                id,
                minuto,
                entity
              );
              list.push(e);
              planillaEventosInput.setValue(list);
            }
            // console.log('list > ', list);
            await saveEvents();
          } catch (error: any) {
            const previousMessage = form.getPreviousMessageForError(rol, id, error);
            showError(previousMessage + error.message);
          }
        }
      }
    } else if (matchIsOver && !enablePenales) {
      showError('No puede asignar eventos luego del final del partido');
    } else if (!periodoStarted) {
      showError('No puede asignar eventos antes de que inicie el periodo');
    }

    if (hasStarted) {
      setTipoEventoSelected(GOL);
    }
  };

  const addEventForJugador = (ppjId: number) => {
    const planillaPartido = planillaPartidoInput.getValue() as PlanillaPartido;
    const ppj = planillaPartido
      .getPlanillaPartidoJugadorList()
      .find((ppj) => ppj.isIdentifiedBy(String(ppjId)));
    if (ppj) addEventForPersona(ROL_JUGADOR, ppjId, ppj);
  };

  const addEventForOficial = (ppoId: number) => {
    const planillaPartido = planillaPartidoInput.getValue() as PlanillaPartido;
    const ppo = planillaPartido
      .getPlanillaPartidoOficialList()
      .find((ppo) => ppo.isIdentifiedBy(String(ppoId)));
    if (ppo) addEventForPersona(ROL_OFICIAL, ppoId, ppo);
  };

  const addEventForOficialAuxiliar = (ppoaId: number) => {
    const planillaPartido = planillaPartidoInput.getValue() as PlanillaPartido;
    const ppoa = planillaPartido
      .getPlanillaPartidoOficialAuxiliarList()
      .find((ppo) => ppo.isIdentifiedBy(String(ppoaId)));
    if (ppoa) addEventForPersona(ROL_OFICIAL_AUXILIAR, ppoaId, ppoa);
  };

  const eventButtonsData = [
    { value: GOL, imageSrc: '/img/gol_2.svg' },
    { value: TARJETA_AMARILLA, imageSrc: '/img/tarjeta_amarilla.svg' },
    { value: DOS_MINUTOS, imageSrc: '/img/dos_minutos.svg' },
    { value: TARJETA_ROJA, imageSrc: '/img/tarjeta_roja.svg' },
    { value: TARJETA_AZUL, imageSrc: '/img/tarjeta_azul.svg' },
  ];

  const addTimeout = (eqId: number) => {
    const planillaPartido = planillaPartidoInput.getValue() as PlanillaPartido;
    const timeouts = [...timeoutPartidoInput.getValue()] as TimeoutXPlanilla[];

    const planillaPartidoId = planillaPartido.getId();
    const currentPeriod = periodos.find((p) => p.isPlaying);
    const minuto = getMinuto();
    if (currentPeriod) {
      if (isRunning || planillaPartido.getDiferido()) {
        try {
          form.validateTimeout(eqId, currentPeriod);

          const timeout = TimeoutXPlanilla.createFrom(
            'new',
            +planillaPartidoId!,
            eqId,
            currentPeriod.tiempo,
            minuto
          );
          timeouts.push(timeout);
          timeoutPartidoInput.setValue(timeouts);
          pause();
          // setIsPaused(true); // esto hace el trigger de saveEvents()
        } catch (error: any) {
          showError(error.message);
        }
      } else {
        showError('El tiempo debe estar corriendo para poder pedir un timeout');
      }
    } else {
      showError('El partido debe comenzar para poder pedir un timeout');
    }
  };

  function addAlpha(color: string, opacity: number) {
    // coerce values so ti is between 0 and 1.
    var _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
    return color + _opacity.toString(16).toUpperCase();
  }

  const tieneEventosSinGuardar = () => {
    if (!isPartidoOnline) {
      return true;
    }
    const eventos = [...planillaEventosInput.getValue()] as EventoXPlanilla[];
    const timeouts = [...timeoutPartidoInput.getValue()] as TimeoutXPlanilla[];
    return (
      eventos.some((e) => !e.getId() || e.getId() === 'new') ||
      timeouts.some((t) => !t.getId() || t.getId() === 'new')
    );
  };

  const noPuedenEmpatar = !form.getPlanillaPartidoInput().getValue().getPuedeEmpatar();
  const estanEmpatando =
    form.getCantidadGolesLocal() +
      +form.getPlanillaPartidoInput().getValue().getPartido()!.getGolesLocal()! ===
    form.getCantidadGolesVisitante() +
      +form.getPlanillaPartidoInput().getValue().getPartido()!.getGolesVisitante()!;

  const descargarPDF = async (planillaPartidoId: string, partidoId: string) => {
    try {
      changeBlockUI(true);

      const planillas = await system.getPlanillaPartidoSystem().getPlanillaPdfDisponibles(partidoId);

      const planilla = planillas.find((pd) => +pd.planillaPartidoId === +planillaPartidoId);
      if (planilla && planilla.hasPdf) {
        await system.getPlanillaPartidoSystem().downloadPdf(planilla?.pdf!.getRutaPdf());
      } else {
        await system.getPlanillaPartidoSystem().generatePdf(planillaPartidoId);
      }

      courier.messageSuccess('El PDF fue generado correctamente');
    } catch (error) {
      console.error('planilla generada');
    } finally {
      changeBlockUI(false);
    }
  };

  return (
    <div className="ms-Grid" dir="ltr" id="no-seleccionable">
      <div className="ms-Grid-row ">
        <div className="ms-Grid-col ms-lg12 lg-mt-2 mb-2 ">
          <div>
            <div className="ms-Grid  mt-2" dir="ltr">
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm10">
                  <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm6 ">
                      <div className="team-card-container">
                        <div
                          className="ms-Grid-col ms-sm12"
                          style={{
                            borderBottomColor: !invertedTeams ? localColor : visitanteColor,
                            borderBottomStyle: 'solid',
                            borderBottomWidth: '5px',
                          }}
                        >
                          <div
                            className="triangulo"
                            style={{ borderBottomColor: !invertedTeams ? localColor : visitanteColor }}
                          ></div>
                          <PlanillaDigitalTeamCard
                            system={system}
                            equipo={!invertedTeams ? form.getEquipoLocal() : form.getEquipoVisitante()}
                            local={!invertedTeams}
                            goles={
                              !invertedTeams ? form.getCantidadGolesLocal() : form.getCantidadGolesVisitante()
                            }
                            color={!invertedTeams ? localColor : visitanteColor}
                          />
                        </div>
                      </div>
                      <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                        {viewMode === 'G' ? (
                          <PlanillaDigitalJugadoresGrid
                            jugadoresForList={
                              !invertedTeams
                                ? form.getJugadoresLocalesForList(getMinuto())
                                : form.getJugadoresVisitantesForList(getMinuto())
                            }
                            addEventForJugador={addEventForJugador}
                            color={!invertedTeams ? localColor : visitanteColor}
                            getMinuto={getMinuto}
                            form={form}
                            esDiferido={esDiferido}
                            onRefereeReview={onRefereeReview}
                          ></PlanillaDigitalJugadoresGrid>
                        ) : (
                          <PlanillaDigitalJugadoresDetailList
                            jugadoresList={
                              !invertedTeams
                                ? form.getJugadoresLocalesForList(getMinuto())
                                : form.getJugadoresVisitantesForList(getMinuto())
                            }
                            addEventForJugador={addEventForJugador}
                            getMinuto={getMinuto}
                            form={form}
                            esDiferido={esDiferido}
                            onRefereeReview={onRefereeReview}
                          />
                        )}
                      </div>
                    </div>
                    <div className="ms-Grid-col ms-sm6 ">
                      <div
                        className="ms-Grid-col ms-sm12"
                        style={{
                          borderBottomColor: !invertedTeams ? visitanteColor : localColor,
                          borderBottomStyle: 'solid',
                          borderBottomWidth: '5px',
                        }}
                      >
                        <div
                          className="triangulo"
                          style={{ borderBottomColor: !invertedTeams ? visitanteColor : localColor }}
                        ></div>
                        <PlanillaDigitalTeamCard
                          system={system}
                          equipo={!invertedTeams ? form.getEquipoVisitante() : form.getEquipoLocal()}
                          local={!invertedTeams ? false : true}
                          goles={
                            !invertedTeams ? form.getCantidadGolesVisitante() : form.getCantidadGolesLocal()
                          }
                          color={!invertedTeams ? visitanteColor : localColor}
                        />
                      </div>
                      <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                        {viewMode === 'G' ? (
                          <PlanillaDigitalJugadoresGrid
                            jugadoresForList={
                              !invertedTeams
                                ? form.getJugadoresVisitantesForList(getMinuto())
                                : form.getJugadoresLocalesForList(getMinuto())
                            }
                            addEventForJugador={addEventForJugador}
                            color={!invertedTeams ? visitanteColor : localColor}
                            getMinuto={getMinuto}
                            form={form}
                            esDiferido={esDiferido}
                            onRefereeReview={onRefereeReview}
                          ></PlanillaDigitalJugadoresGrid>
                        ) : (
                          <PlanillaDigitalJugadoresDetailList
                            jugadoresList={
                              !invertedTeams
                                ? form.getJugadoresVisitantesForList(getMinuto())
                                : form.getJugadoresLocalesForList(getMinuto())
                            }
                            addEventForJugador={addEventForJugador}
                            getMinuto={getMinuto}
                            form={form}
                            esDiferido={esDiferido}
                            onRefereeReview={onRefereeReview}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ms-Grid-row mt-1">
                    {viewDts && (
                      <div className="ms-Grid-col ms-sm12 text-center">
                        <div className="mb-1 bg-white" style={{ padding: '5px' }}>
                          <Text
                            variant="medium"
                            style={{
                              fontWeight: 600,
                            }}
                          >
                            Oficiales
                          </Text>
                        </div>
                      </div>
                    )}

                    {viewDts && (
                      <div id="oficiales-locales" className="ms-Grid-col ms-sm6 text-center">
                        <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <PlanillaDigitalOficialesDetailList
                            addEventForOficial={addEventForOficial}
                            addEventForOficialAuxiliar={addEventForOficialAuxiliar}
                            oficialesList={
                              !invertedTeams
                                ? form.getOficialesLocalesForList()
                                : form.getOficialesVisitantesForList()
                            }
                          ></PlanillaDigitalOficialesDetailList>
                        </div>
                      </div>
                    )}

                    {viewDts && (
                      <div id="oficiales-visitantes" className="ms-Grid-col ms-sm6 text-center">
                        <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <PlanillaDigitalOficialesDetailList
                            addEventForOficial={addEventForOficial}
                            addEventForOficialAuxiliar={addEventForOficialAuxiliar}
                            oficialesList={
                              !invertedTeams
                                ? form.getOficialesVisitantesForList()
                                : form.getOficialesLocalesForList()
                            }
                          ></PlanillaDigitalOficialesDetailList>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="ms-Grid-row mt-1">
                    {(form.getExclusionesVisitantesForList(getMinuto()).length > 0 ||
                      form.getExclusionesLocalesForList(getMinuto()).length > 0) && (
                      <div className="ms-Grid-col ms-sm12 text-center">
                        <div className="mb-1 bg-white" style={{ padding: '5px' }}>
                          <Text
                            variant="medium"
                            style={{
                              fontWeight: 600,
                            }}
                          >
                            Excluídos
                          </Text>
                        </div>
                      </div>
                    )}
                    <div id="exclusiones-locales" className="ms-Grid-col ms-sm6 text-center">
                      {form.getExclusionesLocalesForList(getMinuto()).length > 0 && (
                        <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <PlanillaDigitalExclusionesDetailList
                            exclusionesList={
                              !invertedTeams
                                ? form.getExclusionesLocalesForList(getMinuto())
                                : form.getExclusionesVisitantesForList(getMinuto())
                            }
                            color={!invertedTeams ? localColor : visitanteColor}
                          ></PlanillaDigitalExclusionesDetailList>
                        </div>
                      )}
                    </div>
                    <div id="exclusiones-visitantes" className="ms-Grid-col ms-sm6 text-center">
                      {form.getExclusionesVisitantesForList(getMinuto()).length > 0 && (
                        <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <PlanillaDigitalExclusionesDetailList
                            exclusionesList={
                              !invertedTeams
                                ? form.getExclusionesVisitantesForList(getMinuto())
                                : form.getExclusionesLocalesForList(getMinuto())
                            }
                            color={!invertedTeams ? visitanteColor : localColor}
                          ></PlanillaDigitalExclusionesDetailList>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ms-Grid-row mt-1">
                    {viewTimeouts && (
                      <div className="ms-Grid-col ms-sm12 text-center">
                        <div className="mb-1 bg-white" style={{ padding: '5px' }}>
                          <Text
                            variant="medium"
                            style={{
                              fontWeight: 600,
                            }}
                          >
                            Timeouts
                          </Text>
                        </div>
                      </div>
                    )}

                    {viewTimeouts && (
                      <div id="timeouts-locales" className="ms-Grid-col ms-sm6 text-center">
                        <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <PlanillaDigitalTimeoutDetailList
                            timeoutList={
                              !invertedTeams ? form.getTimeoutsLocal() : form.getTimeoutsVisitante()
                            }
                            color={!invertedTeams ? localColor : visitanteColor}
                          ></PlanillaDigitalTimeoutDetailList>
                        </div>
                      </div>
                    )}

                    {viewTimeouts && (
                      <div id="timeouts-visitantes" className="ms-Grid-col ms-sm6 text-center">
                        <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <PlanillaDigitalTimeoutDetailList
                            timeoutList={
                              !invertedTeams ? form.getTimeoutsVisitante() : form.getTimeoutsLocal()
                            }
                            color={!invertedTeams ? visitanteColor : localColor}
                          ></PlanillaDigitalTimeoutDetailList>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="ms-Grid-row mt-1">
                    {viewGoals && (
                      <div className="ms-Grid-col ms-sm12 text-center">
                        <div className="mb-1 bg-white" style={{ padding: '5px' }}>
                          <Text
                            variant="medium"
                            style={{
                              fontWeight: 600,
                            }}
                          >
                            Goles
                          </Text>
                        </div>
                      </div>
                    )}

                    {viewGoals && (
                      <div id="goles-locales" className="ms-Grid-col ms-sm6 text-center">
                        <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <PlanillaDigitalGoalsDetailList
                            currentPeriod={periodos.find((p) => p.isPlaying)!}
                            goalsList={
                              !invertedTeams
                                ? form.getGolesLocalesForList(periodos)
                                : form.getGolesVisitanteForList(periodos)
                            }
                            color={!invertedTeams ? localColor : visitanteColor}
                          ></PlanillaDigitalGoalsDetailList>
                        </div>
                      </div>
                    )}

                    {viewGoals && (
                      <div id="goles-visitantes" className="ms-Grid-col ms-sm6 text-center">
                        <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <PlanillaDigitalGoalsDetailList
                            currentPeriod={periodos.find((p) => p.isPlaying)!}
                            goalsList={
                              !invertedTeams
                                ? form.getGolesVisitanteForList(periodos)
                                : form.getGolesLocalesForList(periodos)
                            }
                            color={!invertedTeams ? visitanteColor : localColor}
                          ></PlanillaDigitalGoalsDetailList>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="ms-Grid-row mt-1">
                    {viewEvents && (
                      <div className="ms-Grid-col ms-sm12 text-center">
                        <div className="mb-1 bg-white" style={{ padding: '5px' }}>
                          <Text
                            variant="medium"
                            style={{
                              fontWeight: 600,
                            }}
                          >
                            Eventos
                          </Text>
                        </div>
                      </div>
                    )}
                    {viewEvents && (
                      <div className="ms-Grid-col ms-sm12 text-center">
                        <div className="ms-Grid-col ms-sm12 mt-1" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <PlanillaDigitalEventList
                            system={system}
                            courier={courier}
                            changeBlockUI={changeBlockUI}
                            form={form}
                            onBlurCustomInput={onBlurCustomInput}
                            planillaEventosInput={planillaEventosInput}
                            saveEvents={saveEvents}
                            archivoSyncDescargado={props.archivoSyncDescargado}
                            showError={showError}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="ms-Grid-col ms-sm2 text-center">
                  {showFinalizarPeriodo && !matchIsOver && (
                    <div className="ms-Grid-row mb-1">
                      <div className="ms-Grid-col ms-sm12">
                        <div className="text-center" style={{ ...containerStyle, padding: 10 }}>
                          <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 mb-1">
                              <DefaultButton
                                onClick={() => {
                                  finalizarPeriodo();
                                }}
                                style={{ width: '100%' }}
                              >
                                Finalizar periodo
                              </DefaultButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {(shouldShowDescargarArchivoJSON || (matchIsOver && tieneEventosSinGuardar())) && (
                    <div className="ms-Grid-row mb-1">
                      <div className="ms-Grid-col ms-sm12">
                        <div className="text-center" style={{ ...containerStyle, padding: 10 }}>
                          <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 mb-1">
                              <Text variant="mediumPlus" style={{ fontWeight: 500 }}>
                                <Icon
                                  iconName="AlertSolid"
                                  className="blinking-icon error-color"
                                  style={{ position: 'relative', top: 2, marginRight: 2 }}
                                ></Icon>
                                El partido tiene eventos sin guardar
                              </Text>
                            </div>

                            <div className="ms-Grid-col ms-sm12 mb-1">
                              <PrimaryButton
                                onClick={() => {
                                  descargarArchivoJSON(false);
                                }}
                                style={{ width: '100%' }}
                                iconProps={{ iconName: 'Download' }}
                              >
                                Descargar partido
                              </PrimaryButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {matchIsOver && !tieneEventosSinGuardar() && (
                    <div className="ms-Grid-row mb-1">
                      <div className="ms-Grid-col ms-sm12">
                        {!form.getPlanillaPartidoInput().getValue().getSuspendida() && (
                          <div className="text-center" style={{ ...containerStyle, padding: 10 }}>
                            <div className="ms-Grid-row">
                              <div className="ms-Grid-col ms-sm12 mb-1">
                                <Text variant="medium" style={{ fontWeight: 600 }}>
                                  <Icon
                                    className="blinking-icon"
                                    style={{ display: 'inline-block', fontSize: 18, color: '#ff8400' }}
                                    iconName="AlertSolid"
                                  ></Icon>{' '}
                                  <span style={{ position: 'relative', bottom: 4 }}>ARBITROS</span>
                                </Text>
                              </div>

                              {!shouldShowPDF && (
                                <div className="ms-Grid-col ms-sm12 mb-1">
                                  <PrimaryButton
                                    onClick={() => {
                                      setShowFinalizarPartidoDialog(true, FINALIZADO, getMinuto());
                                    }}
                                    style={{ width: '100%' }}
                                    iconProps={{ iconName: 'WaitlistConfirm' }}
                                    disabled={noPuedenEmpatar && estanEmpatando}
                                  >
                                    Confirmar planilla
                                  </PrimaryButton>
                                  {noPuedenEmpatar &&
                                    estanEmpatando &&
                                    +form
                                      .getPlanillaPartidoInput()
                                      .getValue()
                                      .getPartido()!
                                      .getGolesLocal()! +
                                      +form
                                        .getPlanillaPartidoInput()
                                        .getValue()
                                        .getPartido()!
                                        .getGolesVisitante()! !==
                                      0 && (
                                      <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm12">
                                          <Text variant="mediumPlus" style={{ fontWeight: 500 }}>
                                            <Icon
                                              iconName="AlertSolid"
                                              className="blinking-icon error-color"
                                              style={{ position: 'relative', top: 2, marginRight: 2 }}
                                            ></Icon>
                                            Resultado global (
                                            {form.getCantidadGolesLocal() +
                                              +form
                                                .getPlanillaPartidoInput()
                                                .getValue()
                                                .getPartido()!
                                                .getGolesLocal()!}{' '}
                                            -
                                            {form.getCantidadGolesVisitante() +
                                              +form
                                                .getPlanillaPartidoInput()
                                                .getValue()
                                                .getPartido()!
                                                .getGolesVisitante()!}
                                            )
                                          </Text>
                                          <Text style={{ display: 'block' }} variant="mediumPlus">
                                            El partido no puede terminar en empate
                                          </Text>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="ms-Grid-row">
                    {!matchIsOver && !showFinalizarPeriodo && (
                      <div className="ms-Grid-col ms-sm12">
                        {periodos && !form.getPlanillaPartidoInput().getValue().finalizada() && (
                          <PlanillaDigitalTimer
                            form={form}
                            countdownMode={countdownMode}
                            system={system}
                            setHasStarted={setHasStarted}
                            initPartido={iniciarPartido}
                            hasStarted={hasStarted}
                            minutes={minutes}
                            // setMinutes={setMinutes}
                            seconds={seconds}
                            // setSeconds={setSeconds}
                            isPaused={!isRunning}
                            pause={pause}
                            resume={resume}
                            start={start}
                            currentPeriod={periodos.find((p) => p.isPlaying)}
                            periodoSeleccionable={esDiferido}
                            periodos={periodos}
                            setPeriodos={(p: IPeriodos[]) => setPeriodos(p)}
                            showPenales={showPenales}
                            enablePenales={enablePenales}
                            setEnablePenales={setEnablePenales}
                            setShowPenales={setShowPenales}
                            restart={restart}
                            matchIsOver={matchIsOver}
                            setMatchIsOver={setMatchIsOver}
                            periodoStarted={periodoStarted}
                            setPeriodoStarted={setPeriodoStarted}
                            setErrorMsg={(v: string) => setErrorMsg(v)}
                            setShowAlertDialog={(v: boolean) => setShowAlertDialog(v)}
                          />
                        )}
                      </div>
                    )}
                    {!form.getPlanillaPartidoInput().getValue().finalizada() && (
                      <div className="ms-Grid-col ms-sm12 mt-1">
                        {eventButtonsData.map((button, index) => (
                          <PlanillaDigitalEventButtons
                            hasStarted={hasStarted}
                            key={index}
                            value={button.value}
                            imageSrc={button.imageSrc}
                            tipoEventoSelected={tipoEventoSelected}
                            setTipoEventoSelected={(v: string) => setTipoEventoSelected(v)}
                            esDiferido={esDiferido}
                            onRefereeReview={onRefereeReview}
                          />
                        ))}
                      </div>
                    )}
                    {!onRefereeReview && !form.getPlanillaPartidoInput().getValue().finalizada() && (
                      <div className="ms-Grid-col ms-sm12 mt-1">
                        <div className="text-center" style={{ ...containerStyle, padding: 10 }}>
                          <div className="ms-Grid-row">
                            <div className="ms-sm12 mb-1">
                              <Text
                                variant="medium"
                                style={{
                                  fontWeight: 600,
                                }}
                              >
                                Timeouts
                              </Text>
                            </div>
                            <div className="ms-Grid-col ms-lg12 ms-xl6 mb-1">
                              <DefaultButton
                                onClick={() => {
                                  addTimeout(+form.getEquipoLocal().getId()!);
                                }}
                                style={{
                                  border: `2px solid ${localColor}`,
                                  width: '100%',
                                  backgroundColor: addAlpha(localColor, 0.5),
                                }}
                              >
                                Local
                              </DefaultButton>
                            </div>
                            <div className="ms-Grid-col ms-lg12 ms-xl6">
                              <DefaultButton
                                onClick={() => {
                                  addTimeout(+form.getEquipoVisitante().getId()!);
                                }}
                                style={{
                                  border: `2px solid ${visitanteColor}`,
                                  width: '100%',
                                  backgroundColor: addAlpha(visitanteColor, 0.5),
                                }}
                              >
                                Visitante
                              </DefaultButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="ms-Grid-col ms-sm12 mt-1">
                      <div className="text-center" style={{ ...containerStyle, padding: 10 }}>
                        <div className="ms-Grid-row">
                          <div className="ms-Grid-col ms-sm12" style={{ zoom: '85%' }}>
                            <Toggle
                              label="Ver oficiales"
                              inlineLabel
                              checked={viewDts}
                              onChange={() => setViewDts(!viewDts)}
                            />
                          </div>
                          <div className="ms-Grid-col ms-sm12" style={{ zoom: '85%' }}>
                            <Toggle
                              label="Ver timeouts"
                              inlineLabel
                              checked={viewTimeouts}
                              onChange={() => setViewTimeouts(!viewTimeouts)}
                            />
                          </div>
                          <div className="ms-Grid-col ms-sm12" style={{ zoom: '85%' }}>
                            <Toggle
                              label="Ver goles"
                              inlineLabel
                              checked={viewGoals}
                              onChange={() => setViewGoals(!viewGoals)}
                              // disabled
                            />
                          </div>
                          <div className="ms-Grid-col ms-sm12" style={{ zoom: '85%' }}>
                            <Toggle
                              label="Ver eventos"
                              inlineLabel
                              checked={viewEvents}
                              onChange={() => setViewEvents(!viewEvents)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ms-Grid-col ms-sm12 mt-1">
                      {viewEvents && !form.getPlanillaPartidoInput().getValue().finalizada() && (
                        <PlanillaDigitalUndo
                          saveEvents={saveEvents}
                          form={form}
                          showError={showError}
                          archivoSyncDescargado={props.archivoSyncDescargado}
                        ></PlanillaDigitalUndo>
                      )}
                    </div>
                    {(shouldShowPDF || form.getPlanillaPartidoInput().getValue().finalizada()) && (
                      <div className="ms-Grid-col ms-sm12 mt-1">
                        <div className="text-center" style={{ ...containerStyle, padding: 10 }}>
                          <div className="ms-Grid-row">
                            <div className="ms-Grid-col ms-sm12 mb-1">
                              <DefaultButton
                                onClick={() => {
                                  const ppId = form.getPlanillaPartidoInput().getValue().getId();
                                  const parId = form.getPlanillaPartidoInput().getValue().getParid();
                                  descargarPDF(ppId, parId);
                                }}
                                style={{ width: '100%' }}
                                iconProps={{ iconName: 'Print' }}
                              >
                                Imprimir PDF
                              </DefaultButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {((onRefereeReview && form.getPlanillaPartidoInput().getValue().getSuspendida()) ||
                      !matchIsOver) &&
                      !form.getPlanillaPartidoInput().getValue().finalizada() && (
                        <div className="ms-Grid-col ms-sm12 mt-1">
                          <div className="text-center" style={{ ...containerStyle, padding: 10 }}>
                            <div className="ms-Grid-row">
                              <div className="ms-Grid-col ms-sm12 mb-1">
                                <DefaultButton
                                  onClick={() => {
                                    pause();
                                    setShowFinalizarPartidoDialog(true, SUSPENDIDO, getMinuto());
                                  }}
                                  style={{ width: '100%' }}
                                  iconProps={{ iconName: 'Cancel' }}
                                >
                                  Suspender partido
                                </DefaultButton>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
            <Dialog
              hidden={!showAlertDialog}
              onDismiss={toggleShowDialog}
              dialogContentProps={{
                type: DialogType.largeHeader,
                title: (
                  <div>
                    <Icon iconName="StatusErrorFull" style={{ position: 'relative', top: '3px' }} /> Atención
                  </div>
                ),
                styles: {
                  header: {
                    background: '#ff4848',
                  },
                  title: {
                    color: 'white',
                  },
                  content: {
                    borderTop: '4px solid #ff4848',
                  },
                },
              }}
              modalProps={{
                isBlocking: true,
                dragOptions: {
                  moveMenuItemText: 'Move',
                  closeMenuItemText: 'Close',
                  menu: ContextualMenu,
                },
              }}
              minWidth={600}
              // className="ms-motion-scaleDownIn"
              className={showAlertDialog ? 'ms-motion-scaleDownIn' : 'ms-motion-scaleDownOut'}
            >
              <div className="mt-2 text-center">
                <Text variant="xLarge" style={{ fontWeight: 600, fontSize: 38 }}>
                  {errorMsg}
                </Text>
              </div>
              <div className="mt-2 text-center">
                <Text variant="mediumPlus" style={{ fontWeight: 400 }}>
                  Avisar al arbitro, en caso que la sanción sea correcta.
                </Text>
              </div>

              <DialogFooter>
                <DefaultButton onClick={toggleShowDialog} text={'Listo'} />
              </DialogFooter>
            </Dialog>

            <Dialog
              hidden={!showEndDialog}
              onDismiss={toggleShowEndDialog}
              dialogContentProps={{
                type: DialogType.largeHeader,
                title: (
                  <div>
                    <Icon iconName="StatusErrorFull" style={{ position: 'relative', top: '3px' }} /> Atención
                  </div>
                ),
                styles: {
                  header: {
                    background: '#0078D4',
                  },
                  title: {
                    color: 'white',
                  },
                  content: {
                    borderTop: '4px solid #0078D4',
                  },
                },
              }}
              modalProps={{
                isBlocking: true,
                dragOptions: {
                  moveMenuItemText: 'Move',
                  closeMenuItemText: 'Close',
                  menu: ContextualMenu,
                },
              }}
              minWidth={600}
              // className="ms-motion-scaleDownIn"
              className={showEndDialog ? 'ms-motion-scaleDownIn' : 'ms-motion-scaleDownOut'}
            >
              <div className="mt-2 text-center">
                <Text variant="xLarge" style={{ fontWeight: 600, fontSize: 38 }}>
                  {alertMsg}
                </Text>
              </div>

              <DialogFooter>
                <DefaultButton onClick={toggleShowEndDialog} text={'Listo'} />
              </DialogFooter>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanillaDigitalView;

export const COOLDOWN_EVENTO = 1000;
